import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingTab, TabValidation } from '../contexts/EFilingContext';

interface StepDef {
  id: EFilingTab;
  label: string;
  estimate: string;
}

const STEP_DEFS: StepDef[] = [
  { id: 'documents', label: 'Upload Documents', estimate: '~30 sec' },
  { id: 'order-info', label: 'Where to File', estimate: '~30 sec' },
  { id: 'case-details', label: 'About Your Case', estimate: '~1 min' },
  { id: 'court-fees', label: 'Review Fees', estimate: '~30 sec' },
  { id: 'order-details', label: 'Submit', estimate: '~30 sec' },
];

// Consolidated flow (Walter R49 phase 1, ?confirmFlow=1): Where to File +
// About Your Case merge into the single Confirm Your Filing step.
const CONFIRM_STEP_DEFS: StepDef[] = [
  { id: 'documents', label: 'Upload Documents', estimate: '~30 sec' },
  { id: 'confirm', label: 'Confirm Your Filing', estimate: '~2 min' },
  { id: 'court-fees', label: 'Review Fees', estimate: '~30 sec' },
  { id: 'order-details', label: 'Submit', estimate: '~30 sec' },
];

interface WizardStepperProps {
  brand: BrandConfig;
  currentTab: EFilingTab;
  /**
   * The per-step verdict map from `EFilingContext`. It is the ONLY completeness
   * source this component reads (SOR-362/363): the context composes the real
   * Submit gate — a resolved quote plus a stored card when the resolved total is
   * non-zero — into `tabValidation['order-details']`, so a rule added to the
   * shared payment validator reaches the stepper through this map with no
   * second derivation here or in the wizard.
   */
  tabValidation: TabValidation;
  showCourtFeesTab: boolean;
  /** Render the consolidated four-step defs (Walter R49 phase 1). */
  confirmFlowEnabled?: boolean;
  onStepClick: (tab: EFilingTab) => void;
}

const STEP_COMPLETE_COLOR = '#0F9D6B';
const STEP_DEFAULT_COLOR = '#94A3B8';

const WizardStepper: React.FC<WizardStepperProps> = ({
  brand,
  currentTab,
  tabValidation,
  showCourtFeesTab,
  confirmFlowEnabled = false,
  onStepClick,
}) => {
  const visibleSteps = (confirmFlowEnabled ? CONFIRM_STEP_DEFS : STEP_DEFS).filter(
    s => s.id !== 'court-fees' || showCourtFeesTab,
  );

  return (
    <div
      className="px-8 py-5"
      data-testid="efiling-stepper"
      style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}
    >
      <div className="flex items-start justify-between relative">
        {visibleSteps.map((step, index) => {
          const isActive = currentTab === step.id;
          const isComplete = tabValidation[step.id] ?? false;

          // A step is reachable (clickable) if it's complete or if all previous steps are complete
          const allPriorComplete = visibleSteps
            .slice(0, index)
            .every(s => tabValidation[s.id]);
          const isClickable = isComplete || allPriorComplete;

          // Determine step circle colors
          let circleBackground: string;
          let circleColor: string;
          if (isComplete) {
            circleBackground = STEP_COMPLETE_COLOR;
            circleColor = '#FFFFFF';
          } else if (isActive) {
            circleBackground = brand.colors.primary;
            circleColor = brand.colors.onPrimary;
          } else {
            circleBackground = '#E5E7EB';
            circleColor = STEP_DEFAULT_COLOR;
          }

          // Determine label colors
          const labelColor = isActive
            ? brand.colors.text
            : isComplete
              ? STEP_COMPLETE_COLOR
              : STEP_DEFAULT_COLOR;

          // Determine connecting line color (between this step and the next)
          const lineColor = isComplete ? STEP_COMPLETE_COLOR : '#E5E7EB';

          return (
            <React.Fragment key={step.id}>
              {/* Step node */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step.id)}
                className="flex flex-col items-center relative z-10 transition-all"
                style={{
                  cursor: isClickable ? 'pointer' : 'default',
                  opacity: 1,
                  minWidth: '100px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                aria-current={isActive ? 'step' : undefined}
              >
                {/* Circle */}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-200"
                  style={{
                    backgroundColor: circleBackground,
                    color: circleColor,
                    boxShadow: isActive
                      ? `0 0 0 3px ${brand.colors.primary}26`
                      : undefined,
                  }}
                >
                  {isComplete ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>

                {/* Label */}
                <span
                  className="mt-2 text-xs font-medium text-center leading-tight"
                  style={{ color: labelColor }}
                >
                  {step.label}
                </span>

                {/* Time estimate */}
                <span
                  className="mt-0.5 text-[10px]"
                  style={{ color: STEP_DEFAULT_COLOR }}
                >
                  {step.estimate}
                </span>
              </button>

              {/* Connecting line (between steps, not after last) */}
              {index < visibleSteps.length - 1 && (
                <div
                  className="flex-1 mt-4 mx-1"
                  style={{
                    height: '2px',
                    backgroundColor: lineColor,
                    borderRadius: '1px',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WizardStepper;
